import { useCallback } from 'react'
import type { ChangeEvent, MouseEvent } from 'react'
import type { UserProductDesignStore } from '../../UserData/_stores/UserProductDesignStore'
import { alpha, Card, Grid, styled } from '@mui/material'
import classNames from 'classnames'
import type { ProductLayoutType } from '../../UserData/_stores/UserPrefsStore'
import usePreventShiftTextSelection, { hasShiftKey, isInteractiveEventTarget } from '../../Common/_hooks/usePreventShiftTextSelection'
import SelectionCheckbox from '../SelectionCheckbox'

export const ProductCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  position: 'relative',
  overflow: 'hidden',
  background: '#fff',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0px 2px 4px -1px rgba(0, 0, 0, 0.05), 0px 4px 5px 0px rgba(0, 0, 0, 0.1), 0px 1px 10px 0px rgba(0, 0, 0, 0.08)',
  outline: '2px solid transparent',
  transition: 'outline 150ms, background-color 150ms',

  '&.selected': {
    outline: '2px solid ' + theme.palette.primary.main
  },

  '&.error': {
    outline: '2px solid ' + theme.palette.error.main,
    backgroundColor: alpha(theme.palette.error.main, 0.04),
  },

  '&.clickable': {
    cursor: 'pointer',

    'img': {
      userSelect: 'none',
    },

    '&:hover': {
      outline: '5px solid ' + alpha(theme.palette.primary.main, 0.15),

      '&.selected': {
        outline: '5px solid ' + theme.palette.primary.main
      },

      '&.error': {
        outline: '5px solid ' + alpha(theme.palette.error.main, 0.4),
      },
    },
  }
}))

export const ProductThumbnail = styled('img')({
  width: '100%',
  height: 'auto',
  aspectRatio: '1120 / 1000',
  display: 'block'
})

export const ProductThumbnailWrapper = styled('div')({
  position: 'relative',
  width: '100%'
})

export const ProductCardWrapper = styled(Grid)(({ theme }) => ({
  width: '20%',

  ['@media (max-width: ' + theme.breakpoints.values.xl + 'px)']: {
    width: '25%'
  },

  ['@media (max-width: ' + theme.breakpoints.values.lg + 'px)']: {
    width: '33.3%'
  },

  ['@media (max-width: ' + theme.breakpoints.values.md + 'px)']: {
    width: '33.3%'
  },

  ['@media (max-width: ' + theme.breakpoints.values.sm + 'px)']: {
    width: '50%'
  },

  '&.list-view': {
    width: '100%',
    paddingTop: 0,

    '&:last-child': {
      [`${ProductCard}`]: {
        borderBottom: '0'
      }
    },

    [`${ProductCard}`]: {
      borderRadius: '0px',
      boxShadow: 'none',
      borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
      padding: '10px 0',
      alignItems: 'center',
      background: 'transparent',
      flexDirection: 'row',
      outline: 'none',

      '&.selected': {
        outline: 'none',
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
      },

      '&.error': {
        outline: 'none',
        backgroundColor: alpha(theme.palette.error.main, 0.08),
        borderLeft: '4px solid ' + theme.palette.error.main,
      },

      '&.clickable': {
        '&:hover': {
          outline: 'none',
          backgroundColor: alpha(theme.palette.primary.main, 0.2),

          '&.selected': {
            outline: 'none',
            backgroundColor: alpha(theme.palette.primary.main, 0.3),
          },

          '&.error': {
            outline: 'none',
            backgroundColor: alpha(theme.palette.error.main, 0.12),
          },
        },
      }
    },

    [`${ProductThumbnail}`]: {
      width: '80px',
      borderRadius: '15px'
    },

    [`${ProductThumbnailWrapper}`]: {
      width: '80px',
      flexShrink: 0,
    },

    [`${ProductTitle}`]: {
      padding: '0px 15px',
      flex: '1 1 0',
      overflow: 'hidden',
    },

    [`${ButtonsWrapper}`]: {
      padding: '0 0 0 15px'
    }
  }
}))

export const ProductTitle = styled('div')({
  color: 'black',
  fontSize: '1rem',
  lineHeight: '1.3rem',
  fontWeight: 500,
  padding: '10px 15px',
  flexGrow: 1,
  minWidth: 0,
})

export const ProductTitleText = styled('div')({
  display: '-webkit-box',
  overflow: 'hidden',
  overflowWrap: 'anywhere',
  lineClamp: 2,
  maxWidth: '100%',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
})

export const ProductTitleMeta = styled('div')({
  marginTop: '8px'
})

export const ProductThumbnailOverlay = styled('div', {
  shouldForwardProp: prop => prop !== 'overlayAlign',
})<{ overlayAlign?: 'left' | 'right' }>(({ theme, overlayAlign = 'right' }) => ({
  position: 'absolute',
  bottom: theme.spacing(1),
  ...(overlayAlign === 'left' ? {
    left: theme.spacing(1),
  } : {
    right: theme.spacing(1),
  }),
}))

export const ButtonsWrapper = styled('div')({
  display: 'flex',
  flexShrink: 0,
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 0 10px 15px',
  gap: '10px'
})

export const SecondaryActionsWrapper = styled('div')(({ theme }) => ({
  marginLeft: theme.spacing(1),

  '&.view-grid': {
    position: 'absolute',
    top: '5px',
    right: '5px',
    marginLeft: 0,
  }
}))

interface Props {
  view: ProductLayoutType
  item: UserProductDesignStore
  actions?: React.ReactNode
  secondaryActions?: React.ReactNode
  selected?: boolean
  error?: boolean
  onClick?: (item: UserProductDesignStore) => void
  onSelect?: (id: number, shiftKey?: boolean) => void
  onUnselect?: (id: number, shiftKey?: boolean) => void
  title?: React.ReactNode
  caption?: React.ReactNode
  imageOverlay?: React.ReactNode
  imageOverlayAlign?: 'left' | 'right'
}

export default function ProductDesign(props: Props) {
  const title = props.title ?? props.item.get('name')

  const onChangeCheckbox = useCallback((e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (props.onSelect === undefined || props.onUnselect === undefined) return;

    const shiftKey = hasShiftKey(e.nativeEvent)

    if (checked) {
      props.onSelect(props.item.get('id'), shiftKey)
    } else {
      props.onUnselect(props.item.get('id'), shiftKey)
    }
  }, [props.item, props.onSelect, props.onUnselect])

  const onClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
    if (isInteractiveEventTarget(e.target)) {
      return
    }

    if (props.onClick) {
      props.onClick(props.item)
    } else if (props.onSelect !== undefined && props.onUnselect !== undefined) {
      if (props.selected !== true) {
        props.onSelect(props.item.get('id'), e.shiftKey)
      } else {
        props.onUnselect(props.item.get('id'), e.shiftKey)
      }
    }
  }, [props.selected, props.item, props.onClick, props.onSelect, props.onUnselect])

  const onMouseDown = usePreventShiftTextSelection<HTMLDivElement>(props.onSelect !== undefined)

  return <ProductCardWrapper item key={props.item.get('id')} className={props.view === 'list' ? 'list-view' : ''}>
    <ProductCard
      elevation={props.view === 'grid' ? 4 : 0}
      className={classNames({
        selected: props.selected === true,
        error: props.error === true,
        clickable: props.onSelect !== undefined || props.onClick !== undefined,
      })}
      onClick={onClick}
      onMouseDown={onMouseDown}
    >
      {props.onSelect ? <SelectionCheckbox
        checked={props.selected === true}
        onChange={onChangeCheckbox}
        sx={props.view === 'grid' ? {
          position: 'absolute',
          top: '5px',
          left: '5px',
          zIndex: 1,
        } : {
          mr: 2,
        }}
      /> : null}
      <ProductThumbnailWrapper>
        <ProductThumbnail src={props.item.get('thumbnail')} draggable={false} />
        {props.imageOverlay !== undefined && props.imageOverlay !== null ? <ProductThumbnailOverlay overlayAlign={props.imageOverlayAlign}>{props.imageOverlay}</ProductThumbnailOverlay> : null}
      </ProductThumbnailWrapper>
      <ProductTitle>
        <ProductTitleText title={typeof title === 'string' ? title : undefined}>{title}</ProductTitleText>
        {props.caption !== undefined && props.caption !== null ? <ProductTitleMeta>{props.caption}</ProductTitleMeta> : null}
      </ProductTitle>
      {props.actions !== undefined && props.actions !== null ? <ButtonsWrapper>
        {props.actions}
      </ButtonsWrapper> : null}
      {props.secondaryActions !== undefined && props.secondaryActions !== null ? <SecondaryActionsWrapper
        className={classNames({
          'view-grid': props.view === 'grid',
        })}
      >
        {props.secondaryActions}
      </SecondaryActionsWrapper> : null}
    </ProductCard>
  </ProductCardWrapper>
}
